import { Api } from '@square/ignition';
import { useGraphApi } from '@squareup/dex-feature-hooks-graph';
import {
  ApiAside,
  ReferencePage,
  ReferenceItem,
  ReferenceSubItemProps,
} from '@squareup/dex-tech-ui';
import {
  ReferenceHrefBuilders,
  ReferencePath,
  toUrl,
} from '@squareup/dex-tech-utils-routing';
import { HeadWrapper } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box } from '@squareup/dex-ui-shared-base';
import {
  getNextPublicApiReferenceNameOrDefault,
  getNextPublicSquareDocsURL,
} from '@squareup/dex-utils-environment';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import { humanizeName } from '@squareup/dex-utils-text';
import React, { ReactNode, useMemo, useRef } from 'react';

import { SquareVersionPage } from '../../../../shared/SquareVersionPage';

const FilteredApiEndpoints = ({
  api,
  version,
}: {
  api: Api | undefined;
  version: string;
}) => {
  let v1Endpoints: ReferenceSubItemProps[] = [];
  let v2Endpoints: ReferenceSubItemProps[] = [];

  if (api) {
    const { endpoints, id } = api;

    v1Endpoints = endpoints
      .filter(
        (endpoint) =>
          endpoint.isV1 && !endpoint.isDeprecated && !endpoint.isRetired
      )
      .map((endpoint) => ({
        item: {
          // `endpoint` contains getters which cannot be spread, so instead,
          // required properties are explicitly specified below:
          description: endpoint.description,
          httpMethod: endpoint.httpMethod,
          isBeta: endpoint.isBeta,
          isDeprecated: endpoint.isDeprecated,
          isV1: endpoint.isV1,
          path: endpoint.path,
          summary: endpoint.summary,
          title: humanizeName(endpoint.name, { style: 'sentence' }),
        },
        href: ReferenceHrefBuilders.versionApiEndpoint({
          square_version: version,
          api: `${id}-api`,
          endpoint: endpoint.id,
        }),
        key: endpoint.id,
      }));

    v2Endpoints = endpoints
      .filter(
        (endpoint) =>
          !endpoint.isV1 && !endpoint.isDeprecated && !endpoint.isRetired
      )
      .map((endpoint) => ({
        item: {
          // `endpoint` contains getters which cannot be spread, so instead,
          // required properties are explicitly specified below:
          description: endpoint.description,
          httpMethod: endpoint.httpMethod,
          isBeta: endpoint.isBeta,
          isDeprecated: endpoint.isDeprecated,
          isV1: endpoint.isV1,
          path: endpoint.path,
          summary: endpoint.summary,
          title: humanizeName(endpoint.name, { style: 'sentence' }),
        },
        href: ReferenceHrefBuilders.versionApiEndpoint({
          square_version: version,
          api: `${id}-api`,
          endpoint: endpoint.id,
        }),
        key: endpoint.id,
      }));
  }

  return (
    <>
      <ReferenceItem key="v2Endpoints" subItems={v2Endpoints} />
      <ReferenceItem key="v1Endpoints" subItems={v1Endpoints} />
    </>
  );
};

const PageContent = () => {
  const aside = useRef<ApiAside>({
    trackingId: 'unknown',
    content: null,
  });

  const query = useParams();
  const square_version = query?.square_version as string;
  const { api } = useGraphApi();

  const description = api?.summary ?? '';
  const title = api
    ? `${api.name} API - ${getNextPublicApiReferenceNameOrDefault()}`
    : getNextPublicApiReferenceNameOrDefault();

  const backHref = useMemo(() => {
    return ReferenceHrefBuilders.version({
      square_version,
    });
  }, [square_version]);

  if (!api) {
    return null;
  }

  aside.current = {
    trackingId: `square-version-api-${api.id}-endpoints-webhooks`,
    content: <FilteredApiEndpoints api={api} version={square_version} />,
    navigationItems: [
      {
        trackingId: `navigate-to-reference-api-${api.id}-endpoints`,
        href: {
          pathname: ReferencePath.VERSION_API,
          query,
        },
        isActive: true,
        testId: 'endpoints',
        text: 'Endpoints',
      },
      {
        trackingId: `navigate-to-reference-api-${api.id}-webhooks`,
        href: {
          pathname: ReferencePath.VERSION_API_WEBHOOK_INDEX,
          query,
        },
        isActive: false,
        testId: 'webhooks',
        text: 'Webhooks',
      },
    ],
  };

  return (
    <>
      <HeadWrapper
        description={description}
        preconnectUrl={getNextPublicSquareDocsURL()}
        title={title}
        canonicalPath={toUrl(
          ReferenceHrefBuilders.versionApi({
            api: `${api.id}-api`,
            square_version,
          })
        )}
      />
      <ReferencePage
        eyebrow={'Square API'}
        title={api.name}
        subTitle={api.summary}
        description={
          <>
            <Box margin={{ bottom: '2x' }}>
              {api.description && (
                <Markdown markdown={api.description}></Markdown>
              )}
            </Box>
          </>
        }
        backHref={backHref}
        aside={aside.current}
      />
    </>
  );
};
class ReferenceApiPage extends SquareVersionPage {
  public static override routeHandlerName = '[square_version]/[api]';

  protected override getPageContent(): ReactNode {
    return super.getPageContent({
      children: <PageContent />,
    });
  }
}

export default ReferenceApiPage;
